import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as EnvelopeIcon,
  TrendingUp,
  TrendingDown,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  FileCopy as CarryoverIcon
} from '@mui/icons-material';
import { useAppSelector } from '../hooks/redux';
import { envelopeBudgetsApi, categoriesApi } from '../services/api';
import { 
  EnvelopeBudgetSummary, 
  BudgetAllocation,
  CreateBudgetAllocationRequest,
  UpdateBudgetAllocationRequest,
  Category,
  BudgetCarryoverRequest 
} from '../types';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function EnvelopeBudget() {
  const [budgetSummary, setBudgetSummary] = useState<EnvelopeBudgetSummary | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState<BudgetAllocation | null>(null);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [carryoverDialogOpen, setCarryoverDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState<CreateBudgetAllocationRequest>({
    category_id: 0,
    allocated_amount: 0
  });
  const [totalIncome, setTotalIncome] = useState(0);
  const [carryoverForm, setCarryoverForm] = useState({
    copyIncome: false,
    selectedCategories: [] as number[]
  });

  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (user) {
      fetchBudgetData();
      fetchCategories();
    }
  }, [user, selectedMonth, selectedYear]);

  const fetchBudgetData = async () => {
    try {
      setLoading(true);
      const summary = await envelopeBudgetsApi.getEnvelopeBudgetSummary(selectedYear, selectedMonth);
      setBudgetSummary(summary);
      setTotalIncome(summary.total_income);
      setError(null);
    } catch (err) {
      setError('Failed to load budget data');
      console.error('Error fetching budget data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesApi.getCategories();
      setCategories(response);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSetIncome = async () => {
    try {
      if (budgetSummary?.current_period) {
        await envelopeBudgetsApi.updateBudgetPeriod(
          budgetSummary.current_period.id, 
          { total_income: totalIncome }
        );
      } else {
        await envelopeBudgetsApi.createBudgetPeriod({
          month: selectedMonth,
          year: selectedYear,
          total_income: totalIncome
        });
      }
      await fetchBudgetData();
      setIncomeDialogOpen(false);
    } catch (err) {
      console.error('Error setting income:', err);
      setError('Failed to set income');
    }
  };

  const handleOpenAllocationDialog = (allocation?: BudgetAllocation) => {
    if (allocation) {
      setEditingAllocation(allocation);
      setFormData({
        category_id: allocation.category_id,
        allocated_amount: allocation.allocated_amount
      });
    } else {
      setEditingAllocation(null);
      setFormData({
        category_id: getAvailableCategories()[0]?.id || 0,
        allocated_amount: 0
      });
    }
    setDialogOpen(true);
  };

  const handleCloseAllocationDialog = () => {
    setDialogOpen(false);
    setEditingAllocation(null);
  };

  const handleSubmitAllocation = async () => {
    try {
      if (!budgetSummary?.current_period) return;

      if (editingAllocation) {
        await envelopeBudgetsApi.updateAllocation(
          editingAllocation.id, 
          { allocated_amount: formData.allocated_amount }
        );
      } else {
        await envelopeBudgetsApi.createAllocation(
          budgetSummary.current_period.id,
          formData
        );
      }
      await fetchBudgetData();
      handleCloseAllocationDialog();
    } catch (err) {
      console.error('Error saving allocation:', err);
      setError('Failed to save allocation');
    }
  };

  const handleDeleteAllocation = async (allocationId: number) => {
    if (window.confirm('Are you sure you want to delete this allocation?')) {
      try {
        await envelopeBudgetsApi.deleteAllocation(allocationId);
        await fetchBudgetData();
      } catch (err) {
        console.error('Error deleting allocation:', err);
        setError('Failed to delete allocation');
      }
    }
  };

  const handleRefreshSpentAmounts = async () => {
    try {
      await envelopeBudgetsApi.refreshSpentAmounts(selectedYear, selectedMonth);
      await fetchBudgetData();
    } catch (err) {
      console.error('Error refreshing spent amounts:', err);
      setError('Failed to refresh spent amounts');
    }
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleGoToCurrentMonth = () => {
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
  };

  const isCurrentMonth = selectedMonth === currentMonth && selectedYear === currentYear;

  const handleCarryover = async () => {
    try {
      const carryoverRequest: BudgetCarryoverRequest = {
        target_month: selectedMonth,
        target_year: selectedYear,
        copy_income: carryoverForm.copyIncome,
        category_ids: carryoverForm.selectedCategories.length > 0 ? carryoverForm.selectedCategories : undefined
      };

      const response = await envelopeBudgetsApi.carryoverBudget(carryoverRequest);
      await fetchBudgetData();
      setCarryoverDialogOpen(false);
      setCarryoverForm({ copyIncome: false, selectedCategories: [] });
      
      // Show success message
      setError(null);
    } catch (err: any) {
      console.error('Error carrying over budget:', err);
      setError(err.response?.data?.detail || 'Failed to carryover budget');
    }
  };

  const getPreviousMonthData = () => {
    if (selectedMonth === 1) {
      return { month: 12, year: selectedYear - 1 };
    }
    return { month: selectedMonth - 1, year: selectedYear };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getAvailableCategories = () => {
    if (!budgetSummary?.current_period) return categories;
    const allocatedCategoryIds = budgetSummary.current_period.budget_allocations.map(a => a.category_id);
    return categories.filter(cat => !allocatedCategoryIds.includes(cat.id));
  };

  const getProgressColor = (spent: number, allocated: number) => {
    const percentage = (spent / allocated) * 100;
    if (percentage > 100) return 'error';
    if (percentage > 80) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const availableAmount = budgetSummary?.available_to_budget || 0;
  const isOverBudget = availableAmount < 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EnvelopeIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Envelope Budget
          </Typography>
        </Box>
        
        {/* Month Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePreviousMonth} size="small">
            <ChevronLeftIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 200, justifyContent: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                variant="outlined"
              >
                {monthNames.map((month, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                variant="outlined"
              >
                {Array.from({ length: 10 }, (_, i) => currentYear - 5 + i).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <IconButton onClick={handleNextMonth} size="small">
            <ChevronRightIcon />
          </IconButton>
          
          {!isCurrentMonth && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<TodayIcon />}
              onClick={handleGoToCurrentMonth}
              sx={{ ml: 2 }}
            >
              Current
            </Button>
          )}
          
          {!isCurrentMonth && (
            <Chip
              label="Historical"
              color="secondary"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Budget Overview Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 200px' }, minWidth: 200 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Monthly Income
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {formatCurrency(budgetSummary?.total_income || 0)}
              </Typography>
              <Button 
                size="small" 
                onClick={() => setIncomeDialogOpen(true)}
                sx={{ mt: 1 }}
                disabled={!isCurrentMonth}
              >
                {isCurrentMonth ? 'Set Income' : 'View Only'}
              </Button>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 200px' }, minWidth: 200 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Total Allocated
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(budgetSummary?.total_allocated || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 200px' }, minWidth: 200 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Available to Budget
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: isOverBudget ? 'error.main' : 'success.main'
                }}
              >
                {formatCurrency(availableAmount)}
              </Typography>
              {isOverBudget && (
                <Chip 
                  icon={<WarningIcon />}
                  label="Over Budget" 
                  color="error" 
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 200px' }, minWidth: 200 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Categories
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {budgetSummary?.current_period?.budget_allocations?.length || 0}
              </Typography>
              <Button 
                size="small" 
                onClick={handleRefreshSpentAmounts}
                startIcon={<RefreshIcon />}
                sx={{ mt: 1 }}
                disabled={!isCurrentMonth}
              >
                {isCurrentMonth ? 'Refresh' : 'Historical'}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Budget Allocations (Envelopes)
          </Typography>
          {!isCurrentMonth && (
            <Typography variant="body2" color="text.secondary">
              Historical data - editing disabled
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          {isCurrentMonth && !budgetSummary?.current_period?.budget_allocations?.length && (
            <Button
              variant="outlined"
              startIcon={<CarryoverIcon />}
              onClick={() => setCarryoverDialogOpen(true)}
            >
              Carryover
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenAllocationDialog()}
            disabled={getAvailableCategories().length === 0 || !isCurrentMonth}
          >
            Add Category
          </Button>
        </Stack>
      </Box>

      {/* Budget Allocations List */}
      {!budgetSummary?.current_period?.budget_allocations?.length ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <EnvelopeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No budget allocations yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Start by setting your monthly income, then allocate money to different categories
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => setIncomeDialogOpen(true)}
              disabled={!isCurrentMonth}
            >
              {isCurrentMonth ? 'Set Income' : 'View Only'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenAllocationDialog()}
              disabled={getAvailableCategories().length === 0 || !isCurrentMonth}
            >
              Add Category
            </Button>
          </Stack>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {budgetSummary.current_period.budget_allocations.map((allocation) => {
            const progress = (allocation.spent_amount / allocation.allocated_amount) * 100;
            const isOverspent = allocation.spent_amount > allocation.allocated_amount;
            
            return (
              <Box key={allocation.id} sx={{ flex: { xs: '1 1 100%', md: '1 1 45%', lg: '1 1 30%' }, minWidth: 300 }}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: allocation.category?.color || '#999',
                            borderRadius: '50%',
                            mr: 1
                          }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {allocation.category?.name || 'Unknown'}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenAllocationDialog(allocation)}
                          color="primary"
                          disabled={!isCurrentMonth}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteAllocation(allocation.id)}
                          color="error"
                          disabled={!isCurrentMonth}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Allocated: {formatCurrency(allocation.allocated_amount)}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color={isOverspent ? 'error.main' : 'text.secondary'}
                        >
                          Spent: {formatCurrency(allocation.spent_amount)}
                        </Typography>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progress, 100)}
                        color={getProgressColor(allocation.spent_amount, allocation.allocated_amount)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {Math.round(progress)}% used
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: allocation.remaining_amount >= 0 ? 'success.main' : 'error.main'
                          }}
                        >
                          {allocation.remaining_amount >= 0 ? 'Remaining: ' : 'Over by: '}
                          {formatCurrency(Math.abs(allocation.remaining_amount))}
                        </Typography>
                      </Box>
                    </Box>

                    {isOverspent && (
                      <Chip
                        icon={<WarningIcon />}
                        label="Overspent"
                        color="error"
                        size="small"
                      />
                    )}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Set Income Dialog */}
      <Dialog open={incomeDialogOpen} onClose={() => setIncomeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Monthly Income</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Total Monthly Income (CHF)"
            type="number"
            value={totalIncome}
            onChange={(e) => setTotalIncome(Number(e.target.value))}
            sx={{ mt: 2 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncomeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSetIncome} 
            variant="contained"
            disabled={totalIncome <= 0}
          >
            Set Income
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Allocation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseAllocationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAllocation ? 'Edit Budget Allocation' : 'Add Budget Allocation'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {!editingAllocation && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category_id: Number(e.target.value) })}
                >
                  {getAvailableCategories().map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: category.color,
                            borderRadius: '50%'
                          }}
                        />
                        {category.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Allocated Amount (CHF)"
              type="number"
              value={formData.allocated_amount}
              onChange={(e) => setFormData({ ...formData, allocated_amount: Number(e.target.value) })}
              inputProps={{ min: 0, step: 0.01 }}
            />

            {availableAmount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Available to budget: {formatCurrency(availableAmount)}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAllocationDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitAllocation} 
            variant="contained"
            disabled={formData.allocated_amount <= 0 || (!editingAllocation && formData.category_id === 0)}
          >
            {editingAllocation ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Carryover Dialog */}
      <Dialog open={carryoverDialogOpen} onClose={() => setCarryoverDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarryoverIcon color="primary" />
            Carryover Budget from Previous Month
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Copy budget allocations from {monthNames[getPreviousMonthData().month - 1]} {getPreviousMonthData().year} to {monthNames[selectedMonth - 1]} {selectedYear}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={carryoverForm.copyIncome}
                onChange={(e) => setCarryoverForm(prev => ({ ...prev, copyIncome: e.target.checked }))}
              />
            }
            label="Also copy monthly income"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Categories to carryover (leave unchecked to copy all):
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Spent amounts will be reset to $0 for the new month. Only allocation amounts are copied.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCarryoverDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCarryover} 
            variant="contained"
            startIcon={<CarryoverIcon />}
          >
            Carryover Budget
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}